import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, Volume2, VolumeX } from 'lucide-react';
import { useTutorial } from './TutorialContext';

/**
 * TutorialOverlay — the actual on-screen tour UI.
 *
 * Rendered globally (once, in App.js) above the router. When `isActive` is
 * true it:
 *   1. Navigates to `step.route` if the user isn't already there
 *   2. Locates `[data-testid=step.target_testid]` on the current page,
 *      computes its bounding rect, and paints a dark backdrop with a
 *      spotlight cutout over it (SVG mask — simplest cross-browser way
 *      to punch a rectangular hole in a full-viewport dim).
 *   3. Renders a tooltip card near the target with title, body, controls,
 *      and an <audio> element playing the pre-generated SAGE voice-over.
 *
 * Steps with `target_testid == null` are rendered center-screen with no
 * spotlight (welcome / farewell).
 *
 * Behaviour choices worth naming:
 *   - The card is `position: fixed` so it survives scroll.
 *   - We poll for the target element every 120ms up to 3s per step —
 *     dashboard cards animate/mount just after route change, so we can't
 *     read their rect synchronously.
 *   - Audio autoplays muted-safely — most browsers allow it because the
 *     user opened the tour with a click. If a browser blocks autoplay the
 *     mute-toggle icon lets them tap-to-play.
 *   - Body scroll is locked while the tour is active so the spotlight
 *     stays aligned. `overflow: hidden` on <body>.
 */

// Small helper — read a bounding client rect for a testid, or null.
const rectFor = (testid) => {
  if (!testid) return null;
  const el = document.querySelector(`[data-testid="${testid}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  // If the element has 0 area (display:none, collapsed), treat as missing.
  if (r.width <= 0 || r.height <= 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
};

// Pad the spotlight so the highlight breathes around the element edges.
const SPOTLIGHT_PAD = 8;

// Whether two rectangles overlap.
const rectsOverlap = (a, b) => {
  return !(a.left + a.width <= b.left ||
           b.left + b.width <= a.left ||
           a.top + a.height <= b.top ||
           b.top + b.height <= a.top);
};

// Compute an (unclamped) card position for one side of the target rect.
const rawPositionFor = (rect, side, cardW, cardH) => {
  switch (side) {
    case 'top':
      return {
        top: rect.top - cardH - 16,
        left: rect.left + rect.width / 2 - cardW / 2,
      };
    case 'bottom':
      return {
        top: rect.top + rect.height + 16,
        left: rect.left + rect.width / 2 - cardW / 2,
      };
    case 'left':
      return {
        top: rect.top + rect.height / 2 - cardH / 2,
        left: rect.left - cardW - 16,
      };
    case 'right':
    default:
      return {
        top: rect.top + rect.height / 2 - cardH / 2,
        left: rect.left + rect.width + 16,
      };
  }
};

// Given the target rect + card size + placement hint, choose card top/left
// in fixed coordinates so it sits alongside the highlighted element and
// stays inside the viewport. If the requested side would push the card
// off-screen and cause it to overlap the target after clamping, try the
// opposite (and orthogonal) sides in order before falling back to center.
const positionCard = (rect, placement, cardW, cardH) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const margin = 20;
  if (!rect || placement === 'center') {
    return {
      top: Math.max(margin, (vh - cardH) / 2),
      left: Math.max(margin, (vw - cardW) / 2),
    };
  }
  // Try the requested side first, then fall back through the perpendicular
  // + opposite sides. This handles cases like: wins-tracker is close to
  // the left edge, so placement='left' would push the card off-screen —
  // 'right' is the right answer instead.
  const preference = {
    left: ['left', 'right', 'top', 'bottom'],
    right: ['right', 'left', 'top', 'bottom'],
    top: ['top', 'bottom', 'right', 'left'],
    bottom: ['bottom', 'top', 'right', 'left'],
  }[placement] || ['right', 'left', 'top', 'bottom'];

  for (const side of preference) {
    const raw = rawPositionFor(rect, side, cardW, cardH);
    // Fits in viewport unclamped? Take it if it also doesn't overlap the target.
    const fits =
      raw.top >= margin &&
      raw.left >= margin &&
      raw.top + cardH <= vh - margin &&
      raw.left + cardW <= vw - margin;
    if (!fits) continue;
    const cardRect = { top: raw.top, left: raw.left, width: cardW, height: cardH };
    if (!rectsOverlap(cardRect, rect)) {
      return { top: raw.top, left: raw.left };
    }
  }

  // No side fit cleanly — clamp the originally-requested side and accept
  // whatever overlap results (better than being off-screen).
  const raw = rawPositionFor(rect, placement, cardW, cardH);
  const top = Math.min(Math.max(margin, raw.top), vh - cardH - margin);
  const left = Math.min(Math.max(margin, raw.left), vw - cardW - margin);
  return { top, left };
};

const TutorialOverlay = () => {
  const {
    isActive,
    steps,
    stepIndex,
    audioUrl,
    nextStep,
    prevStep,
    closeTutorial,
  } = useTutorial();

  const navigate = useNavigate();
  const location = useLocation();

  // The current step, and its resolved target rect (may be null).
  const step = steps[stepIndex] || null;
  const [targetRect, setTargetRect] = useState(null);
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);
  const cardRef = useRef(null);

  // Card size for placement math. Measured after render; a sensible default
  // avoids first-paint jump.
  const [cardSize, setCardSize] = useState({ w: 380, h: 240 });

  // Resolved outline geometry. Always rendered as SVG rects — we can't
  // wrap them in a `{cutout && ...}` conditional because the preview
  // tooling injects <span display:contents> wrappers around conditionally
  // rendered JSX, and browsers won't render <rect> inside a <span>. Instead
  // we always emit the rect and drive visibility via stroke-opacity, which
  // is a valid presentation attribute the tooling doesn't wrap.
  const outline = targetRect
    ? {
        x: Math.max(0, targetRect.left - SPOTLIGHT_PAD),
        y: Math.max(0, targetRect.top - SPOTLIGHT_PAD),
        w: targetRect.width + SPOTLIGHT_PAD * 2,
        h: targetRect.height + SPOTLIGHT_PAD * 2,
        visible: true,
      }
    : { x: -100, y: -100, w: 1, h: 1, visible: false };

  // Navigate to `step.route` when the step changes (only if we're not
  // already there). React-router `replace: false` so a Back button in the
  // browser doesn't kick the user out of the tour mid-flow.
  useEffect(() => {
    if (!isActive || !step) return;
    if (step.route && location.pathname !== step.route) {
      navigate(step.route);
    }
  }, [isActive, step, navigate, location.pathname]);

  // Poll for the target element until we find it (bounded to ~3s). This
  // handles route transitions where the target hasn't mounted yet.
  useEffect(() => {
    if (!isActive || !step) {
      setTargetRect(null);
      return undefined;
    }
    if (!step.target_testid) {
      setTargetRect(null);
      return undefined;
    }

    let tries = 0;
    let stopped = false;
    const tick = () => {
      if (stopped) return;
      const r = rectFor(step.target_testid);
      if (r) {
        setTargetRect(r);
        return;
      }
      tries += 1;
      if (tries < 25) {
        setTimeout(tick, 120);
      } else {
        // Gave up — center the card without a spotlight rather than
        // stalling the tour.
        setTargetRect(null);
      }
    };
    tick();
    return () => {
      stopped = true;
    };
  }, [isActive, step, location.pathname]);

  // Recompute rect on scroll/resize so the spotlight follows.
  useEffect(() => {
    if (!isActive || !step || !step.target_testid) return undefined;
    const recompute = () => {
      const r = rectFor(step.target_testid);
      if (r) setTargetRect(r);
    };
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [isActive, step]);

  // Measure the tooltip card after it renders.
  useEffect(() => {
    if (!isActive || !cardRef.current) return;
    const r = cardRef.current.getBoundingClientRect();
    if (r.width && r.height) {
      setCardSize((prev) => {
        if (Math.abs(prev.w - r.width) < 2 && Math.abs(prev.h - r.height) < 2) {
          return prev;
        }
        return { w: r.width, h: r.height };
      });
    }
  }, [isActive, stepIndex, step]);

  // Lock body scroll while active so the spotlight stays aligned.
  useEffect(() => {
    if (!isActive) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isActive]);

  // Autoplay audio whenever the URL changes. If the browser rejects
  // autoplay (rare — the user clicked to start the tour), the mute button
  // becomes a play-toggle via the same ref.
  useEffect(() => {
    if (!isActive) return;
    const el = audioRef.current;
    if (!el || !audioUrl) return;
    el.muted = muted;
    el.currentTime = 0;
    const p = el.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        // Autoplay blocked — leave the UI in place; the user can tap
        // the volume icon to unmute+play.
      });
    }
  }, [audioUrl, isActive, muted]);

  const cardPos = useMemo(
    () => positionCard(targetRect, step?.placement || 'center', cardSize.w, cardSize.h),
    [targetRect, step, cardSize.w, cardSize.h],
  );

  if (!isActive || !step) return null;

  const total = steps.length;
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === total - 1;

  // (The `outline` object was computed earlier — always emitted as SVG
  // rects to avoid the preview tooling's <span> wrapper around conditional
  // JSX which would break SVG rendering.)

  return (
    <div
      className="fixed inset-0 z-[9998] pointer-events-none"
      data-testid="tutorial-overlay"
      aria-live="polite"
    >
      {/* Spotlight backdrop.
          Per user preference the page is NOT dimmed — the underlying UI
          stays fully visible/legible. The tour points at the target with
          only two SVG elements: a blurred amber outer glow (SVG
          feGaussianBlur, gives a soft halo you can see even against dark
          UI), and a crisp cream inner ring on top for definition.

          Both rects are ALWAYS emitted (no `{cutout && ...}` conditional)
          because the emergent preview tooling injects <span display:contents>
          around conditionally-rendered JSX, and browsers won't render
          <rect> inside a <span>. We hide the outline when there's no
          target by setting stroke-opacity=0 via the `outline.visible`
          flag — that's a plain attribute the tooling doesn't wrap.

          The SVG itself still spans the viewport with pointer-events on
          so accidental clicks during a tour don't slip through to page
          elements (e.g. clicking a card and route-changing mid-step) —
          the tour must be advanced via the tooltip controls. */}
      <svg
        className="fixed inset-0 w-full h-full pointer-events-auto"
        onClick={(e) => {
          e.stopPropagation();
        }}
        aria-hidden="true"
      >
        <defs>
          {/* Gaussian blur filter turns a solid amber stroke into a soft
              glowing halo just outside the crisp ring. */}
          <filter
            id="sage-tutorial-glow-filter"
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
          >
            <feGaussianBlur in="SourceGraphic" stdDeviation="4" />
          </filter>
        </defs>

        {/* Layer 1: broad blurred halo. */}
        <rect
          x={outline.x}
          y={outline.y}
          width={outline.w}
          height={outline.h}
          rx="10"
          ry="10"
          fill="none"
          stroke="rgb(252, 211, 77)"
          strokeOpacity={outline.visible ? 0.9 : 0}
          strokeWidth="10"
          filter="url(#sage-tutorial-glow-filter)"
        />

        {/* Layer 2: crisp bright amber ring on top for definition. */}
        <rect
          x={outline.x}
          y={outline.y}
          width={outline.w}
          height={outline.h}
          rx="10"
          ry="10"
          fill="none"
          stroke="rgb(252, 211, 77)"
          strokeOpacity={outline.visible ? 1 : 0}
          strokeWidth="3"
        />
      </svg>

      {/* Tooltip card */}
      <div
        ref={cardRef}
        className="fixed pointer-events-auto max-w-[92vw] w-[380px] bg-stone-950/95 backdrop-blur border border-white/15 rounded-sm shadow-2xl p-6"
        style={{ top: cardPos.top, left: cardPos.left }}
        role="dialog"
        aria-label={`Tutorial step ${stepIndex + 1} of ${total}: ${step.title}`}
        data-testid="tutorial-card"
      >
        {/* Header row: step counter + close */}
        <div className="flex items-center justify-between mb-3">
          <span
            className="text-[10px] font-mono uppercase tracking-widest text-zinc-500"
            data-testid="tutorial-step-counter"
          >
            SAGE onboarding — {stepIndex + 1} / {total}
          </span>
          <button
            type="button"
            onClick={() => closeTutorial({ markComplete: true })}
            className="text-zinc-500 hover:text-white transition-colors"
            aria-label="Close tutorial"
            data-testid="tutorial-close-button"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Title */}
        <h3
          className="text-2xl text-white mb-3"
          style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 300 }}
          data-testid="tutorial-title"
        >
          {step.title}
        </h3>

        {/* Body */}
        <p className="text-sm text-zinc-300 leading-relaxed mb-6" data-testid="tutorial-body">
          {step.body}
        </p>

        {/* Controls row */}
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setMuted((m) => !m)}
            className="text-zinc-400 hover:text-white transition-colors p-1"
            aria-label={muted ? 'Unmute SAGE' : 'Mute SAGE'}
            aria-pressed={muted}
            data-testid="tutorial-mute-button"
          >
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={prevStep}
              disabled={isFirst}
              className="frost-button rounded-sm px-3 py-1.5 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Previous step"
              data-testid="tutorial-prev-button"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              Back
            </button>
            <button
              type="button"
              onClick={() => closeTutorial({ markComplete: true })}
              className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors px-2 py-1.5"
              data-testid="tutorial-skip-button"
            >
              {isLast ? 'Close' : 'Skip'}
            </button>
            <button
              type="button"
              onClick={nextStep}
              className="threshold-button rounded-sm px-4 py-1.5 text-sm"
              data-testid="tutorial-next-button"
            >
              <span>{isLast ? 'Finish' : 'Next'}</span>
              <span className="threshold-button-arrow" aria-hidden="true">→</span>
            </button>
          </div>
        </div>

        {/* Audio element (invisible) */}
        {audioUrl && (
          <audio
            ref={audioRef}
            src={audioUrl}
            preload="auto"
            data-testid="tutorial-audio"
          />
        )}
      </div>
    </div>
  );
};

export default TutorialOverlay;
